import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import _, { create } from 'lodash'
import {
  dataConnectorsConfigUrl,
  shipstationConfigUrl,
  shipsuranceConfigUrl,
  sharedAccountInvitationUrl,
  updateInvitePermissionUrl,
  preferencesUrl,
  profileUrl,
  mfaUrl,
  userAPIKeyUrl,
  updateSeatAssignmentUrl,
  inviteAvailableToolsUrl,
  uploadedImagesUrl,
} from 'urls'
import { getAxiosDefaultConfig } from '../../modules/utils'
import { resolveProfileImageDisplayUrl } from '../../modules/profileImageUrl'
import {
  switchOrganization,
  switchToHomeWorkspace,
  createOrganization,
} from '../../features/organizations/organizationsSlice'

export const initialState = {
  sellApiTokenStatus: {},
  sellApiTokenFetched: false,
  dataConnectors: {
    slackConfigs: {
      status: 'idle',
      error: null,
      data: {
        subscriptions: {
          cancellations: { title: 'Cancellations', value: true },
          recurring_charges: { title: 'Charges', value: true },
          payment_charges: { title: 'Payment', value: true },
          failed_charges: { title: 'Failed Charges', value: true },
          new_subscriptions: { title: 'New Subscriptions', value: true },
        },
        paypal: {
          Purchases: { title: 'Purchases', value: true },
        },
        shopify: {
          ItemsDelivered: { title: 'Items Delivered', value: true },
          ItemsShipped: { title: 'Items Shipped', value: true },
          OrderConfirmed: { title: 'Order Confirmed', value: true },
        },
        bigcommerce: {
          Completed: { title: 'Completed', value: true },
          Shipped: { title: 'Items Shipped', value: true },
          'Awaiting Fulfillment': { title: 'Awaiting Fulfillment', value: true },
          'Awaiting Payment': { title: 'Awaiting Payment', value: true },
          'Partially Shipped': { title: 'Partially Shipped', value: true },
        },
      },
    },
    shipStationConfigs: {
      status: 'idle',
      error: null,
      data: [],
    },
    shipsuranceConfigs: {
      status: 'idle',
      error: null,
      data: [],
    }
  },
  teamAccounts: {
    status: 'idle',
    error: null,
    data: [],
  },
  /** MCP tools catalog for team permission UI (GET /invite/tools/available). */
  availableTools: {
    status: 'idle',
    error: null,
    data: [],
  },
  preferences: {
    status: 'idle',
    error: null,
    data: {
      defaultTimezone: null,
      defaultCurrency: 'USD',
      currencySettings: {},
      shopifySettings: {},
      bigcommerceSettings: {},
      amazonSettings: {},
      walmartSettings: {},
      contacts: {},
      socialAccounts: {},
      shippingSettings: {},
    },
  },
  profile: {
    status: 'idle',
    error: null,
    data: null,
  },
  mfa: {
    status: 'idle',
    error: null,
    data: null,
  },
}

export const fetchSlackConfigs = createAsyncThunk('appSettings/fetchSlackConfigs', async () => {
  let res = await axios.get(dataConnectorsConfigUrl, getAxiosDefaultConfig())
  return _.isEmpty(res?.data?.slackconfig) ? initialState.dataConnectors.slackConfigs.data : res?.data?.slackconfig
})

export const updateSlackConfigs = createAsyncThunk('appSettings/updateSlackConfigs', async (payload) => {
  let res = await axios.put(dataConnectorsConfigUrl, { slackConfig: payload }, getAxiosDefaultConfig())
  return res?.data?.data
})

export const fetchShipStationConfigs = createAsyncThunk('appSettings/fetchShipStationConfigs', async () => {
  let res = await axios.get(shipstationConfigUrl, getAxiosDefaultConfig())
  return res?.data
})

export const updateShipStationConfigs = createAsyncThunk('appSettings/updateShipStationConfigs', async (payload) => {
  let res = await axios.put(shipstationConfigUrl, payload, getAxiosDefaultConfig())
  return res?.data?.data
})

export const fetchShipsuranceConfigs = createAsyncThunk('appSettings/fetchShipsuranceConfigs', async () => {
  let res = await axios.get(shipsuranceConfigUrl, getAxiosDefaultConfig())
  return res?.data
})

export const updateShipsuranceConfigs = createAsyncThunk('appSettings/updateShipsuranceConfigs', async (payload) => {
  let res = await axios.put(shipsuranceConfigUrl, payload, getAxiosDefaultConfig())
  return res?.data?.data
})

export const fetchTeamAccounts = createAsyncThunk('appSettings/fetchTeamAccounts', async () => {
  let res = await axios.get(sharedAccountInvitationUrl, getAxiosDefaultConfig())
  return res?.data
})

export const fetchAvailableTools = createAsyncThunk('appSettings/fetchAvailableTools', async () => {
  const res = await axios.get(inviteAvailableToolsUrl, getAxiosDefaultConfig())
  return Array.isArray(res?.data) ? res.data : []
})

export const teamInviteErrorMessage = (error: unknown, email: string): string => {
  const code = typeof error === 'string' ? error : (error as Error)?.message || ''
  switch (code) {
    case 'duplicate user':
      return `You have already invited ${email}`
    case 'invite email address missing':
      return `${email} is not a valid email address`
    default:
      return `Unable to invite ${email}. Please try again.`
  }
}

export const addTeamAccount = createAsyncThunk(
  'appSettings/addTeamAccount',
  async (invited: string, { rejectWithValue }) => {
    try {
      const res = await axios.post(sharedAccountInvitationUrl, { invited }, getAxiosDefaultConfig())
      // POST /invite ends with returnAccountsShared: full team list, not a single row
      return Array.isArray(res?.data) ? res.data : []
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to send invite')
    }
  }
)

export const updateTeamAccount = createAsyncThunk(
  'appSettings/updateTeamAccount',
  async ({ invited, accessPermission }) => {
    const res = await axios.post(updateInvitePermissionUrl, { invited, accessPermission }, getAxiosDefaultConfig())
    return res?.data
  }
)

export const removeTeamAccount = createAsyncThunk('appSettings/removeTeamAccount', async (invited) => {
  let res = await axios.delete(sharedAccountInvitationUrl, {
    ...getAxiosDefaultConfig(),
    data: { invited },
  })
  return invited
})

export const updateSeatAssignment = createAsyncThunk(
  'appSettings/updateSeatAssignment',
  async ({ shared_with, seatAssigned, owner }: { shared_with: string; seatAssigned: boolean; owner: string }, { rejectWithValue }) => {
    try {
      let res = await axios.post(updateSeatAssignmentUrl, { shared_with, seatAssigned, owner }, getAxiosDefaultConfig())
      return { shared_with, seatAssigned }
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Failed to update seat assignment'
      return rejectWithValue(message)
    }
  }
)

export const fetchPreferences = createAsyncThunk('appSettings/fetchPreferences', async () => {
  let res = await axios.get(preferencesUrl, getAxiosDefaultConfig())
  return res?.data
})

export const updatePreferences = createAsyncThunk('appSettings/updatePreferences', async (payload) => {
  let res = await axios.post(preferencesUrl, payload, getAxiosDefaultConfig())

  return res?.data
})

export const createUserAPIKey = createAsyncThunk('appSettings/createUserAPIKey', async (payload) => {
  let res = await axios.post(userAPIKeyUrl, payload, getAxiosDefaultConfig())
  return res?.data?.data
})

const PROFILE_IMAGE_RESOLUTION_STORAGE_PREFIX = 'profileImageResolutionAttempted_'

export const fetchProfile = createAsyncThunk('appSettings/fetchProfile', async (_, { getState }) => {
  const state = getState()
  const userEmail = state?.root?.user?.email
  const storageKey = userEmail ? `${PROFILE_IMAGE_RESOLUTION_STORAGE_PREFIX}${userEmail}` : null
  const skipResolution =
    typeof localStorage !== 'undefined' && storageKey && localStorage.getItem(storageKey) === 'true'

  const config = {
    ...getAxiosDefaultConfig(),
    params: { _t: Date.now() },
  }
  if (skipResolution) {
    config.headers = { ...config.headers, 'X-Skip-Profile-Image-Resolution': 'true' }
  }

  const res = await axios.get(profileUrl, config)

  if (res.headers['x-profile-image-resolution-attempted'] === 'true' && storageKey) {
    try {
      localStorage.setItem(storageKey, 'true')
    } catch (_) {}
  }

  return res?.data
})

export const updateProfile = createAsyncThunk(
  'appSettings/updateProfile',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axios.put(profileUrl, payload, getAxiosDefaultConfig())
      return res?.data
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to update profile'
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to update profile')
    }
  }
)

/**
 * Password and MFA belong to the person, not the org workspace account, so in an
 * org session these requests opt into personal scope.
 */
const personalWhenInOrg = (state: any) =>
  personalScopeConfig(!!state?.organizations?.isWorkspaceContext)

export const updateProfilePassword = createAsyncThunk('appSettings/updateProfilePassword', async (payload, { getState }) => {
  const res = await axios.put(`${profileUrl}/password`, payload, personalWhenInOrg(getState()))
  return res?.data
})

export interface ProfileImageCandidate {
  source: string
  label: string
  url: string
}

/** Photo actions on the Profile page target the person, not the org workspace account. */
const personalScopeConfig = (personal?: boolean) => {
  const config = getAxiosDefaultConfig()
  return personal
    ? { ...config, headers: { ...config.headers, 'X-Profile-Scope': 'personal' } }
    : config
}

export const fetchProfileImageCandidates = createAsyncThunk(
  'appSettings/fetchProfileImageCandidates',
  async (personal: boolean | undefined, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${profileUrl}/image-candidates`, personalScopeConfig(personal))
      return ((res?.data?.candidates || []) as ProfileImageCandidate[]).map((candidate) => ({
        ...candidate,
        url: resolveProfileImageDisplayUrl(candidate.url) || candidate.url,
      }))
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to load image options'
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to load image options')
    }
  }
)

export const uploadProfileImage = createAsyncThunk(
  'appSettings/uploadProfileImage',
  async ({ file, personal }: { file: File; personal?: boolean }, { rejectWithValue }) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await axios.post(uploadedImagesUrl, formData, {
        ...personalScopeConfig(personal),
        headers: {
          ...personalScopeConfig(personal).headers,
          'Content-Type': 'multipart/form-data',
        },
      })
      return res?.data as { profileImageUrl?: string }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to upload image'
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to upload image')
    }
  }
)

export const selectProfileImage = createAsyncThunk(
  'appSettings/selectProfileImage',
  async (
    { profileImageUrl, personal }: { profileImageUrl: string; personal?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const res = await axios.put(
        `${profileUrl}/image`,
        { profileImageUrl },
        personalScopeConfig(personal)
      )
      return res?.data as { profileImageUrl?: string }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to set image'
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to set image')
    }
  }
)

export const removeProfileImage = createAsyncThunk(
  'appSettings/removeProfileImage',
  async (personal: boolean | undefined, { rejectWithValue }) => {
    try {
      const uploadedRes = await axios
        .delete(`${uploadedImagesUrl}/current`, personalScopeConfig(personal))
        .catch(() => null)
      if (uploadedRes?.data?.profileImageUrl === null) {
        return { profileImageUrl: null }
      }
      const res = await axios.delete(`${profileUrl}/image`, personalScopeConfig(personal))
      return res?.data as { profileImageUrl?: string | null }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to remove image'
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to remove image')
    }
  }
)

export const disconnectProvider = createAsyncThunk(
  'appSettings/disconnectProvider',
  async (provider: string, { dispatch, getState }) => {
    await axios.delete(
      `${profileUrl}/connected-providers/${encodeURIComponent(provider)}`,
      personalWhenInOrg(getState())
    )
    dispatch(fetchProfile())
  }
)

/**
 *
 * MFA
 *
 */
export const fetchMfaStatus = createAsyncThunk('appSettings/fetchMfaStatus', async (_, { rejectWithValue, getState }) => {
  try {
    const res = await axios.get(`${mfaUrl}/status`, personalWhenInOrg(getState()))
    return res?.data
  } catch (e: any) {
    return rejectWithValue(e?.response?.data?.error || e?.message || 'Failed to load MFA status')
  }
})

export const setupEmailMfa = createAsyncThunk('appSettings/setupEmailMfa', async (_, { rejectWithValue, getState }) => {
  try {
    const res = await axios.post(`${mfaUrl}/email/setup`, {}, personalWhenInOrg(getState()))
    return res?.data as { challengeToken: string; expiresInSeconds?: number }
  } catch (e: any) {
    return rejectWithValue(e?.response?.data?.error || 'Failed to start email verification setup')
  }
})

export const resendEmailMfaSetupCode = createAsyncThunk(
  'appSettings/resendEmailMfaSetupCode',
  async (challengeToken: string, { rejectWithValue, getState }) => {
    try {
      const res = await axios.post(`${mfaUrl}/email/setup/resend`, { challengeToken }, personalWhenInOrg(getState()))
      return res?.data as { message: string; expiresInSeconds?: number }
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.error || 'Failed to resend code')
    }
  }
)

export const confirmEmailMfa = createAsyncThunk(
  'appSettings/confirmEmailMfa',
  async (payload: { challengeToken: string; code: string }, { rejectWithValue, getState }) => {
    try {
      const res = await axios.post(`${mfaUrl}/email/confirm`, payload, personalWhenInOrg(getState()))
      return res?.data as { mfaEnabled: boolean; method: string }
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.error || 'Incorrect code. Please try again.')
    }
  }
)

export const setupTotp = createAsyncThunk('appSettings/setupTotp', async (_, { rejectWithValue, getState }) => {
  try {
    const res = await axios.post(`${mfaUrl}/totp/setup`, {}, personalWhenInOrg(getState()))
    return res?.data as { secretBase32: string; qrCodeDataUrl: string }
  } catch (e: any) {
    return rejectWithValue(e?.response?.data?.error || 'Failed to start authenticator setup')
  }
})

export const confirmTotp = createAsyncThunk(
  'appSettings/confirmTotp',
  async (code: string, { rejectWithValue, getState }) => {
    try {
      const res = await axios.post(`${mfaUrl}/totp/confirm`, { code }, personalWhenInOrg(getState()))
      return res?.data as { mfaEnabled: boolean; method: string; recoveryCodes: string[] }
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.error || 'Incorrect code. Please try again.')
    }
  }
)

export const requestMfaReverifyCode = createAsyncThunk(
  'appSettings/requestMfaReverifyCode',
  async (_, { rejectWithValue, getState }) => {
    try {
      const res = await axios.post(`${mfaUrl}/reverify/send-code`, {}, personalWhenInOrg(getState()))
      return res?.data as { challengeToken: string; method: string; expiresInSeconds?: number }
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.error || 'Failed to start re-verification')
    }
  }
)

export const disableMfa = createAsyncThunk(
  'appSettings/disableMfa',
  async (
    payload: { password: string; challengeToken: string; code: string; useRecoveryCode?: boolean },
    { rejectWithValue, getState }
  ) => {
    try {
      const res = await axios.post(`${mfaUrl}/disable`, payload, personalWhenInOrg(getState()))
      return res?.data
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.error || 'Failed to disable MFA')
    }
  }
)

export const regenerateRecoveryCodes = createAsyncThunk(
  'appSettings/regenerateRecoveryCodes',
  async (
    payload: { password: string; challengeToken: string; code: string; useRecoveryCode?: boolean },
    { rejectWithValue, getState }
  ) => {
    try {
      const res = await axios.post(`${mfaUrl}/recovery-codes/regenerate`, payload, personalWhenInOrg(getState()))
      return res?.data as { recoveryCodes: string[] }
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.error || 'Failed to regenerate recovery codes')
    }
  }
)

export const dismissMfaPrompt = createAsyncThunk('appSettings/dismissMfaPrompt', async (_, { getState }) => {
  await axios.post(`${mfaUrl}/prompt/dismiss`, {}, personalWhenInOrg(getState()))
})

export const settingsSlice = createSlice({
  name: 'appSettings', // temporary name until we can remove the old settings state altogether
  initialState,
  reducers: {
    resetTeamAccounts: (state) => {
      state.teamAccounts = { status: 'idle', error: null, data: [] }
    },
    patchProfileImageUrl: (state, action) => {
      if (!action.payload) return
      state.profile.data = {
        ...(state.profile.data || {}),
        profileImageUrl: resolveProfileImageDisplayUrl(action.payload) ?? action.payload,
      }
      state.profile.status = 'succeeded'
    },
  },
  extraReducers(builder) {
    builder
      /**
       *
       * slackConfigs api
       *
       */
      .addCase(fetchSlackConfigs.pending, (state, action) => {
        state.dataConnectors.slackConfigs.status = 'loading'
      })
      .addCase(fetchSlackConfigs.fulfilled, (state, action) => {
        state.dataConnectors.slackConfigs.status = 'succeeded'
        state.dataConnectors.slackConfigs.data = action.payload
      })
      .addCase(fetchSlackConfigs.rejected, (state, action) => {
        state.dataConnectors.slackConfigs.status = 'failed'
        state.dataConnectors.slackConfigs.error = action.error.message
      })
      .addCase(updateSlackConfigs.fulfilled, (state, action) => {
        state.dataConnectors.slackConfigs.data = action.payload
      })

      /**
       *
       * shipStationConfigs api
       *
       */
      .addCase(fetchShipStationConfigs.pending, (state, action) => {
        state.dataConnectors.shipStationConfigs.status = 'loading'
      })
      .addCase(fetchShipStationConfigs.fulfilled, (state, action) => {
        state.dataConnectors.shipStationConfigs.status = 'succeeded'
        state.dataConnectors.shipStationConfigs.data = action.payload
      })
      .addCase(fetchShipStationConfigs.rejected, (state, action) => {
        state.dataConnectors.shipStationConfigs.status = 'failed'
        state.dataConnectors.shipStationConfigs.error = action.error.message
      })
      .addCase(updateShipStationConfigs.fulfilled, (state, action) => {
        state.dataConnectors.shipStationConfigs.data = action.payload
      })

      /**
       *
       * shipsuranceConfigs api
       *
       */
      .addCase(fetchShipsuranceConfigs.pending, (state, action) => {
        state.dataConnectors.shipsuranceConfigs.status = 'loading'
      })
      .addCase(fetchShipsuranceConfigs.fulfilled, (state, action) => {
        state.dataConnectors.shipsuranceConfigs.status = 'succeeded'
        state.dataConnectors.shipsuranceConfigs.data = action.payload
      })
      .addCase(fetchShipsuranceConfigs.rejected, (state, action) => {
        state.dataConnectors.shipsuranceConfigs.status = 'failed'
        state.dataConnectors.shipsuranceConfigs.error = action.error.message
      })
      .addCase(updateShipsuranceConfigs.fulfilled, (state, action) => {
        state.dataConnectors.shipsuranceConfigs.data = action.payload
      })

      /**
       *
       * teamAccounts api
       *
       */
      .addCase(fetchTeamAccounts.pending, (state, action) => {
        state.teamAccounts.status = 'loading'
      })
      .addCase(fetchTeamAccounts.fulfilled, (state, action) => {
        state.teamAccounts.status = 'succeeded'
        state.teamAccounts.data = action.payload
      })
      .addCase(fetchTeamAccounts.rejected, (state, action) => {
        state.teamAccounts.status = 'failed'
        state.teamAccounts.error = action.error.message
      })
      .addCase(addTeamAccount.fulfilled, (state, action) => {
        state.teamAccounts.data = action.payload
      })
      .addCase(updateTeamAccount.fulfilled, (state, action) => {
        // Same as invite POST: server returns full sharingWith list from user_shared_accounts
        if (Array.isArray(action.payload)) {
          state.teamAccounts.data = action.payload
        } else if (action.payload?.shared_with) {
          const index = _.findIndex(state.teamAccounts.data, { shared_with: action.payload.shared_with })
          if (index !== -1) {
            state.teamAccounts.data[index] = action.payload
          }
        }
      })
      .addCase(removeTeamAccount.fulfilled, (state, action) => {
        state.teamAccounts.data = _.reject(
          state.teamAccounts.data,
          (teamAccount) => teamAccount.shared_with === action.meta.arg
        )
      })
      .addCase(switchOrganization.fulfilled, (state) => {
        state.teamAccounts = { status: 'idle', error: null, data: [] }
      })
      .addCase(switchToHomeWorkspace.fulfilled, (state) => {
        state.teamAccounts = { status: 'idle', error: null, data: [] }
      })
      .addCase(createOrganization.fulfilled, (state) => {
        state.teamAccounts = { status: 'idle', error: null, data: [] }
      })
      .addCase(fetchAvailableTools.pending, (state) => {
        state.availableTools.status = 'loading'
        state.availableTools.error = null
        // Keep previous catalog during refetch so My Team expand rows don’t flash empty.
      })
      .addCase(fetchAvailableTools.fulfilled, (state, action) => {
        state.availableTools.status = 'succeeded'
        state.availableTools.data = action.payload
      })
      .addCase(fetchAvailableTools.rejected, (state, action) => {
        state.availableTools.status = 'failed'
        state.availableTools.error = action.error.message ?? 'Failed to load tools'
        state.availableTools.data = []
      })
      .addCase(updateSeatAssignment.fulfilled, (state, action) => {
        const { shared_with, seatAssigned } = action.payload
        const index = _.findIndex(state.teamAccounts.data, { shared_with })
        if (index !== -1) {
          state.teamAccounts.data[index].seat_assigned = seatAssigned
        }
      })

      /**
       *
       * preferences api
       *
       */
      .addCase(fetchPreferences.pending, (state, action) => {
        state.preferences.status = 'loading'
      })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.preferences.status = 'succeeded'
        state.preferences.data = action.payload
      })
      .addCase(fetchPreferences.rejected, (state, action) => {
        state.preferences.status = 'failed'
        state.preferences.error = action.error.message
      })
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.preferences.data = action.payload
      })

      /**
       * profile api
       */
      .addCase(fetchProfile.pending, (state) => {
        state.profile.status = 'loading'
        state.profile.error = null
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.profile.status = 'succeeded'
        const payload = action.payload as {
          profileImageUrl?: string | null
          personalProfileImageUrl?: string | null
        } | null
        state.profile.data = payload
          ? {
              ...payload,
              profileImageUrl: resolveProfileImageDisplayUrl(payload.profileImageUrl) ?? null,
              personalProfileImageUrl:
                resolveProfileImageDisplayUrl(payload.personalProfileImageUrl) ?? null,
            }
          : payload
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.profile.status = 'failed'
        state.profile.error = action.error?.message ?? null
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (state.profile.data) {
          state.profile.data = { ...state.profile.data, ...action.payload }
        }
      })
      // Personal-scope photo results go to personalProfileImageUrl so the org logo
      // (profileImageUrl, shown in the sidebar) is never overwritten.
      .addCase(uploadProfileImage.fulfilled, (state, action) => {
        if (action.payload?.profileImageUrl) {
          const key = action.meta.arg.personal ? 'personalProfileImageUrl' : 'profileImageUrl'
          state.profile.data = {
            ...(state.profile.data || {}),
            [key]:
              resolveProfileImageDisplayUrl(action.payload.profileImageUrl) ??
              action.payload.profileImageUrl,
          }
          state.profile.status = 'succeeded'
        }
      })
      .addCase(selectProfileImage.fulfilled, (state, action) => {
        if (state.profile.data && action.payload?.profileImageUrl) {
          const key = action.meta.arg.personal ? 'personalProfileImageUrl' : 'profileImageUrl'
          state.profile.data = {
            ...state.profile.data,
            [key]:
              resolveProfileImageDisplayUrl(action.payload.profileImageUrl) ??
              action.payload.profileImageUrl,
          }
        }
      })
      .addCase(removeProfileImage.fulfilled, (state, action) => {
        if (state.profile.data) {
          const key = action.meta.arg ? 'personalProfileImageUrl' : 'profileImageUrl'
          state.profile.data = { ...state.profile.data, [key]: null }
        }
      })
      .addCase(updateProfilePassword.fulfilled, (state) => {
        if (state.profile.data) {
          state.profile.data = { ...state.profile.data, hasPassword: true }
        }
      })
      .addCase(disconnectProvider.fulfilled, (state, action) => {
        if (state.profile.data?.connectedProviders) {
          state.profile.data = {
            ...state.profile.data,
            connectedProviders: state.profile.data.connectedProviders.filter(
              (p) => p.provider !== action.meta.arg
            ),
          }
        }
      })

      /**
       *
       * mfa api
       *
       */
      .addCase(fetchMfaStatus.pending, (state) => {
        state.mfa.status = 'loading'
      })
      .addCase(fetchMfaStatus.fulfilled, (state, action) => {
        state.mfa.status = 'succeeded'
        state.mfa.data = action.payload
      })
      .addCase(fetchMfaStatus.rejected, (state, action) => {
        state.mfa.status = 'failed'
        state.mfa.error = action.payload as string
      })
      .addCase(confirmEmailMfa.fulfilled, (state, action) => {
        state.mfa.data = { ...(state.mfa.data || {}), ...action.payload, shouldPromptSetup: false }
      })
      .addCase(confirmTotp.fulfilled, (state, action) => {
        state.mfa.data = {
          ...(state.mfa.data || {}),
          mfaEnabled: action.payload.mfaEnabled,
          method: action.payload.method,
          totpConfirmed: true,
          shouldPromptSetup: false,
        }
      })
      .addCase(disableMfa.fulfilled, (state) => {
        state.mfa.data = {
          ...(state.mfa.data || {}),
          mfaEnabled: false,
          method: null,
          totpConfirmed: false,
        }
      })
      .addCase(dismissMfaPrompt.fulfilled, (state) => {
        if (state.mfa.data) {
          state.mfa.data = { ...state.mfa.data, shouldPromptSetup: false }
        }
      })
  },
})

export const { resetTeamAccounts, patchProfileImageUrl } = settingsSlice.actions

/**
 *
 * Selectors
 *
 */
export const selectSlackConfigStatus = (state) => state.appSettings.dataConnectors.slackConfigs.status
export const selectSlackConfig = (state) => state.appSettings.dataConnectors.slackConfigs.data
export const selectShipStationConfigStatus = (state) => state.appSettings.dataConnectors.shipStationConfigs.status
export const selectShipStationConfig = (state) => state.appSettings.dataConnectors.shipStationConfigs.data
export const selectShipsuranceConfigStatus = (state) => state.appSettings.dataConnectors.shipsuranceConfigs.status
export const selectShipsuranceConfig = (state) => state.appSettings.dataConnectors.shipsuranceConfigs.data
export const selectTeamAccountsStatus = (state) => state.appSettings.teamAccounts.status
export const selectTeamAccounts = (state) => state.appSettings.teamAccounts.data
export const selectAvailableToolsStatus = (state) => state.appSettings.availableTools.status
export const selectAvailableTools = (state) => state.appSettings.availableTools.data
export const selectAvailableToolsError = (state) => state.appSettings.availableTools.error
export const selectPreferencesStatus = (state) => state.appSettings.preferences.status
export const selectPreferences = (state) => state.appSettings.preferences.data
export const selectProfileStatus = (state) => state.appSettings.profile.status
export const selectProfile = (state) => state.appSettings.profile.data
export const selectProfileError = (state) => state.appSettings.profile.error
export const selectMfaStatus = (state) => state.appSettings.mfa.status
export const selectMfa = (state) => state.appSettings.mfa.data
export const selectMfaError = (state) => state.appSettings.mfa.error

export default settingsSlice.reducer
